import { useState, useEffect, useMemo, useCallback } from "react";
import useAuth from "../../../Hooks/useAuth";
import { useGetPanelActivityQuery } from "../../../store";
import {
  PageShell,
  PageHeader,
  ListSkeleton,
  EmptyState,
  ListView,
  ListToolbar,
} from "../../../Components/shared";
import { Pagination, DateRangeField } from "../../../Components/atoms";
import {
  FaClipboardList,
  FaCheckCircle,
  FaTimesCircle,
  FaDesktop,
} from "react-icons/fa";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import trLocale from "date-fns/locale/tr";

// Fabrika → Programlama İşlemleri.
// Okkalı Servis panelindeki "Programlama Görevlisi" hesaplarının faaliyet logu:
// kim, ne zaman, ne yaptı, ne kadar sürdü, başardı mı.
// Contract: BayinerIOT-Workspace-/docs/api-contracts/panel-activity.md
//
// Bu liste bir İZLENEBİLİRLİK kaydı DEĞİLDİR — kayıtta ürünün kimliği (seri no,
// parça kodu, ünite id) yoktur. "Bir Çayset anakartı programlandı" der, "şu kart
// programlandı" demez. İzlenebilirlik Fabrika → Parçalar/Makineler sayfalarında.
//
// FİLTRELER SUNUCUDAN: sayfalama server tarafında olduğu için filtre de orada
// olmak zorunda. Client'ta filtrelemek YANLIŞ olurdu — yalnız o sayfadaki 25
// kaydı süzer, toplam sayaç ve sayfa sayısı tutmazdı. Bu yüzden sözleşmenin
// desteklediği üç filtre var: userEmail, from, to. Sonuç (✓/✗) ve kategori
// için sunucuda karşılığı yok, sekme de eklenmedi.

const PAGE_SIZE = 25;

const TABLE_COLUMNS = [
  { label: "Tarih", width: "w-44", skeletonWidth: "w-28" },
  { label: "Kişi", width: "w-56", skeletonWidth: "w-32" },
  { label: "İşlem", width: "", skeletonWidth: "w-40", skeletonExtra: true },
  { label: "Sonuç", width: "w-20", skeletonWidth: "w-12" },
  { label: "Süre", width: "w-24", skeletonWidth: "w-14" },
  { label: "Bilgisayar", width: "w-44", skeletonWidth: "w-24" },
];

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

// Süre saniye gelir. 0 geçerli bir değer (anlık işlem) — bu yüzden null/undefined
// kontrolü `!sure` ile YAPILMAZ, 0 da "-" olurdu.
const formatDuration = (sureSn) => {
  if (sureSn == null || !Number.isFinite(Number(sureSn))) return "-";
  const s = Math.max(0, Math.round(Number(sureSn)));
  if (s < 60) return `${s} sn`;
  const dk = Math.floor(s / 60);
  const kalan = s % 60;
  return kalan ? `${dk} dk ${kalan} sn` : `${dk} dk`;
};

function SuccessBadge({ basarili }) {
  return basarili === false ? (
    <FaTimesCircle className="w-4 h-4 text-red-500" title="Başarısız" />
  ) : (
    <FaCheckCircle className="w-4 h-4 text-emerald-500" title="Başarılı" />
  );
}

function ProgramlamaIslemleriPage() {
  const { auth } = useAuth();
  const token = auth.accessToken;

  const [emailInput, setEmailInput] = useState("");
  const [debouncedEmail, setDebouncedEmail] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Arama debounce — her tuş vuruşunda istek atılmasın
  useEffect(() => {
    const t = setTimeout(() => setDebouncedEmail(emailInput.trim()), 400);
    return () => clearTimeout(t);
  }, [emailInput]);

  // Filtre değişince ilk sayfaya dön — yoksa 5. sayfada daralan bir sonuç
  // kümesinde boş liste görünür.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedEmail, startDate, endDate]);

  // Bitiş tarihi gün SONUNA çekilir: kullanıcı "21 Ağustos"u seçtiğinde o günün
  // kayıtları da dahil olsun (aksi halde 00:00'da kesilir).
  const endOfDay = (d) => {
    const dt = new Date(d);
    dt.setHours(23, 59, 59, 999);
    return dt.toISOString();
  };

  const queryArgs = useMemo(
    () => ({
      token,
      page: currentPage,
      limit: PAGE_SIZE,
      userEmail: debouncedEmail || undefined,
      from: startDate ? new Date(startDate).toISOString() : undefined,
      to: endDate ? endOfDay(endDate) : undefined,
    }),
    [token, currentPage, debouncedEmail, startDate, endDate],
  );

  const { data, isLoading, isFetching } = useGetPanelActivityQuery(queryArgs, {
    skip: !token,
  });

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const loading = isLoading || isFetching;

  const stats = useMemo(
    () => [{ icon: FaClipboardList, value: total, label: "İşlem", color: "blue" }],
    [total],
  );

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    const tableContainer = document.querySelector(".overflow-y-auto");
    if (tableContainer) tableContainer.scrollTop = 0;
  }, []);

  const dateFilterNode = (
    <DateRangeField
      startDate={startDate}
      setStartDate={setStartDate}
      endDate={endDate}
      setEndDate={setEndDate}
      size="sm"
      zIndex={10050}
      className="flex flex-col sm:flex-row gap-2 [&>div]:sm:w-44"
    />
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
      <PageShell>
        <div className="px-3 py-2.5 sm:p-4 md:p-6 bg-gray-50/80 sm:bg-gray-50 shrink-0">
          <PageHeader
            icon={FaClipboardList}
            iconColor="blue"
            title="Programlama İşlemleri"
            subtitle="Atölyedeki programlama görevlilerinin işlem geçmişi — kim, ne zaman, ne yaptı"
            stats={stats}
            isLoading={loading}
            mobileSlotId="panel-activity-toolbar-mobile-slot"
          />
        </div>

        <div className="bg-white flex-1 min-h-0 flex flex-col">
          <ListToolbar
            className="px-3 pb-2.5 sm:px-4 sm:pb-4 md:px-6 md:pb-6"
            mobileFilterSlotId="panel-activity-toolbar-mobile-slot"
            search={dateFilterNode}
            searchFields={[
              {
                placeholder: "Kullanıcı E-posta",
                value: emailInput,
                onChange: (e) => setEmailInput(e.target.value),
              },
            ]}
            onClear={() => {
              setEmailInput("");
              setStartDate(null);
              setEndDate(null);
              setCurrentPage(1);
            }}
          />

          <ListView
            headers={TABLE_COLUMNS.map((c) => ({
              label: c.label,
              className: c.width,
            }))}
            data={items}
            // Kayıtta id yok (audit'ten türetilmiş düz nesne) — sayfa içinde
            // benzersiz bir anahtar için zaman + işlem + sıra birleştirilir.
            rowKey={(row, index) => `${row.zaman}-${row.islem}-${index}`}
            renderRow={(row, index) => (
              <tr
                key={`${row.zaman}-${row.islem}-${index}`}
                className="bg-white hover:bg-gray-50 transition-colors"
              >
                <td className="px-4 py-3 text-sm text-gray-500">
                  {formatDate(row.zaman)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-800 font-Medium truncate">
                  {row.kullanici || "-"}
                  {row.kullanici_email && (
                    <div className="text-xs text-gray-400 font-Regular truncate">
                      {row.kullanici_email}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-800 truncate">
                  {row.islem || "-"}
                </td>
                <td className="px-4 py-3">
                  <SuccessBadge basarili={row.basarili} />
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {formatDuration(row.sure_sn)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 truncate">
                  {row.bilgisayar ? (
                    <span className="inline-flex items-center gap-1.5">
                      <FaDesktop className="w-3 h-3 text-gray-400 shrink-0" />
                      {row.bilgisayar}
                    </span>
                  ) : (
                    "-"
                  )}
                </td>
              </tr>
            )}
            renderMobileCard={(row, index) => (
              <div
                key={`${row.zaman}-${row.islem}-${index}`}
                className="bg-white px-3.5 py-3 border-b border-gray-200"
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="text-sm text-gray-800 font-Medium truncate">
                    {row.islem || "-"}
                  </span>
                  <SuccessBadge basarili={row.basarili} />
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {row.kullanici || row.kullanici_email || "-"}
                </div>
                <div className="flex items-center justify-between mt-1.5 text-xs text-gray-400">
                  <span className="truncate">
                    {formatDuration(row.sure_sn)}
                    {row.bilgisayar ? ` · ${row.bilgisayar}` : ""}
                  </span>
                  <span>{formatDate(row.zaman)}</span>
                </div>
              </div>
            )}
            isLoading={loading}
            loadingNode={
              <div className="relative p-3 sm:p-4 md:p-6">
                <ListSkeleton
                  columns={TABLE_COLUMNS}
                  rowCount={10}
                  mobileCardCount={6}
                  message="Programlama işlemleri yükleniyor..."
                />
              </div>
            }
            empty={
              <div className="p-4 md:p-6">
                <EmptyState
                  message="Kayıt bulunamadı"
                  suggestion="Seçili filtrelere uygun programlama işlemi yok. Kayıtlar 90 gün saklanır."
                />
              </div>
            }
            footer={
              totalPages > 1 ? (
                <div className="shrink-0 border-t border-gray-200 bg-white">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    itemsPerPage={PAGE_SIZE}
                    totalItems={total}
                    showInfo={true}
                    disabled={loading}
                  />
                </div>
              ) : null
            }
          />
        </div>
      </PageShell>
    </LocalizationProvider>
  );
}

export default ProgramlamaIslemleriPage;
